import type { SagaAuditLogDto } from '@/dtos/ErrorHandlingSaga'
import type { SagaWorkflowGraphDto } from '@/dtos/SagaWorkflow'
import { MarkerType, type Edge } from '@xyflow/react'
import type { AuditFlowEdge } from './auditEdge'
import { ADJACENT_CURVATURE, DASH_PATTERN, edgeTier, MARKER_SIZE, SKIP_CURVATURE } from './edgeStyle'
import { layoutNodes } from './layout'
import type { StateFlowNode } from './nodeTypes'
import type { SelfFlowEdge } from './selfLoopEdge'

const transitionKey = (from: string, to: string) => `${from}->${to}`

// Group audit rows by the transition (PreviousState → NewState) they represent — the steps a hovered edge reveals.
export const bucketStepsByTransition = (steps: SagaAuditLogDto[]): Map<string, SagaAuditLogDto[]> => {
  const byTransition = new Map<string, SagaAuditLogDto[]>()
  for (const s of steps) {
    if (!s.previousState || !s.newState) continue
    const key = transitionKey(s.previousState, s.newState)
    const bucket = byTransition.get(key) ?? []
    bucket.push(s)
    byTransition.set(key, bucket)
  }
  return byTransition
}

// The unpositioned nodes + forward (cross-state) edges, each edge carrying its matching audit rows once.
const buildRawGraph = (graph: SagaWorkflowGraphDto, stepsByTransition: Map<string, SagaAuditLogDto[]>) => {
  const nodes: StateFlowNode[] = graph.nodes.map(n => ({
    id: n.state,
    type: 'state',
    position: { x: 0, y: 0 },
    draggable: false,
    data: { label: n.label, kind: n.kind, status: n.status, current: n.state === graph.currentState },
  }))

  const edges: AuditFlowEdge[] = graph.edges
    .filter(e => e.from !== e.to)
    .map((e, i) => ({
      id: `${e.from}__${e.to}__${i}`,
      source: e.from,
      target: e.to,
      type: 'auditEdge',
      // No animation: a taken edge is completed history, not in-flight — the current position shows on the active node.
      data: { label: e.label || undefined, steps: stepsByTransition.get(transitionKey(e.from, e.to)) ?? [] },
    }))

  return { nodes, edges }
}

// Style + route one laid-out forward edge. Cross-column edges ride the Left→Right backbone; same-column edges go
// Top↔Bottom so they drop through the inter-lane gap instead of sweeping back across the node body.
const styleForwardEdge = (
  e: AuditFlowEdge, currentState: string,
  colById: Map<string, number>, yById: Map<string, number>,
): AuditFlowEdge => {
  const tier = edgeTier((e.data?.steps.length ?? 0) > 0, e.source === currentState)

  const span = (colById.get(e.target) ?? 0) - (colById.get(e.source) ?? 0)
  let sourceHandle = 'r'
  let targetHandle = 'l'
  if (span === 0) {
    const down = (yById.get(e.target) ?? 0) >= (yById.get(e.source) ?? 0)
    sourceHandle = down ? 'bs' : 'ts'
    targetHandle = down ? 'tt' : 'bt'
  }

  return {
    ...e,
    sourceHandle,
    targetHandle,
    data: { ...e.data, steps: e.data?.steps ?? [], curvature: Math.abs(span) >= 2 ? SKIP_CURVATURE : ADJACENT_CURVATURE },
    style: { stroke: tier.stroke, strokeWidth: tier.strokeWidth, strokeDasharray: tier.dashed ? DASH_PATTERN : undefined },
    zIndex: tier.zIndex,
  }
}

// Self-transitions render as a loop arc above the node (excluded from layout): a declared self-loop (retry → retry),
// or any state with same-state audit rows (a retry, or a failed delivery that stayed put). Same tiers as forward edges.
const buildSelfEdges = (graph: SagaWorkflowGraphDto, stepsByTransition: Map<string, SagaAuditLogDto[]>): SelfFlowEdge[] => {
  const selfLoopLabels = new Map(graph.edges.filter(e => e.from === e.to).map(e => [e.from, e.label || undefined]))

  return graph.nodes
    .filter(n => selfLoopLabels.has(n.state) || (stepsByTransition.get(transitionKey(n.state, n.state))?.length ?? 0) > 0)
    .map((n, i) => {
      const steps = stepsByTransition.get(transitionKey(n.state, n.state)) ?? []
      const tier = edgeTier(steps.length > 0, n.state === graph.currentState)
      return {
        id: `self__${n.state}__${i}`,
        source: n.state,
        target: n.state,
        sourceHandle: 'r',
        targetHandle: 'l',
        type: 'selfLoopEdge',
        markerEnd: { type: MarkerType.ArrowClosed, width: MARKER_SIZE, height: MARKER_SIZE, color: tier.stroke },
        style: { stroke: tier.stroke, strokeWidth: tier.strokeWidth, strokeDasharray: tier.dashed ? DASH_PATTERN : undefined },
        zIndex: tier.zIndex,
        data: { label: selfLoopLabels.get(n.state), steps },
      }
    })
}

// Build the positioned React Flow nodes + edges for one saga graph (forward edges + self-loops, layout applied).
export const buildFlow = (graph: SagaWorkflowGraphDto): { nodes: StateFlowNode[]; edges: Edge[] } => {
  const stepsByTransition = bucketStepsByTransition(graph.steps)
  const { nodes: rawNodes, edges: rawEdges } = buildRawGraph(graph, stepsByTransition)

  const { nodes, colById } = layoutNodes(rawNodes, rawEdges)
  const yById = new Map(nodes.map(n => [n.id, n.position.y]))

  const forward = rawEdges.map(e => styleForwardEdge(e, graph.currentState, colById, yById))
  const selfEdges = buildSelfEdges(graph, stepsByTransition)
  return { nodes, edges: [...forward, ...selfEdges] }
}
